import { PERMISSIONS_EXPORT_COLUMN_KEYS } from "./permissions.constants";
import type { HttpRequestConfig, IHttpClient } from "@/core/interfaces";
import {
  collectAllPages,
  extractItems,
  extractTotal,
  isRecord,
  readFirstText,
  readText,
  toPaginatedQuery,
} from "@/core/utils";
import {
  appendExportColumns,
  appendReportQuery,
  parseContentDispositionFileName,
} from "@/modules/export";
import type { ExportedFile } from "@/modules/export";
import { Registry } from "@/shared/infra/dependency-injection/registry";
import type {
  ExportPermissionsParams,
  ListPermissionsParams,
  Permission,
  PermissionTreeClaim,
  PermissionTreeNode,
  PermissionsPage,
  UpdatePermissionParams,
} from "./permissions.types";
import { DEFAULT_PERMISSION_SORT } from "./permissions.sort";

export const PERMISSION_PATHS = {
  LIST: "/api/plataforma/permissoes",
  EXPORT: "/api/plataforma/permissoes/exportar",
} as const;

const COLLECTION_KEYS = ["permissoes"];

export function buildPermissionPath(id: string): string {
  return `${PERMISSION_PATHS.LIST}/${encodeURIComponent(id)}`;
}

function getHttpClient(): IHttpClient {
  return Registry.getInstance().inject<IHttpClient>("HttpClient");
}

/** Desembrulha o envelope do BFF (`{ success, data }`) até chegar ao recurso. */
function extractResource(payload: unknown): unknown {
  if (!isRecord(payload)) return payload;

  for (const key of ["data", "permissao"]) {
    const nested = payload[key];
    if (isRecord(nested)) return extractResource(nested);
  }

  return payload;
}

function toTreeClaim(raw: unknown): PermissionTreeClaim | null {
  if (!isRecord(raw)) return null;

  const claim = readText(raw.claim);
  if (!claim) return null;

  return { claim, value: readText(raw.value), label: readText(raw.label) || claim };
}

function toTreeNode(raw: unknown): PermissionTreeNode | null {
  if (!isRecord(raw)) return null;

  const key = readText(raw.key);
  if (!key) return null;

  return {
    key,
    name: readText(raw.name) || key,
    isGroupingOnly: raw.isGroupingOnly === true,
    children: toTree(raw.children),
    claims: (Array.isArray(raw.claims) ? raw.claims : [])
      .map(toTreeClaim)
      .filter((claim): claim is PermissionTreeClaim => claim !== null),
  };
}

function toTree(raw: unknown): PermissionTreeNode[] {
  return (Array.isArray(raw) ? raw : [])
    .map(toTreeNode)
    .filter((node): node is PermissionTreeNode => node !== null);
}

/** Árvore de módulos do contrato: `meta.arvore` (ou `arvore` na raiz, como o bff também aceita do core). */
function extractTree(payload: unknown): PermissionTreeNode[] {
  if (!isRecord(payload)) return [];
  const meta = isRecord(payload.meta) ? payload.meta : {};
  return toTree(meta.arvore ?? payload.arvore);
}

function toPermission(raw: unknown): Permission | null {
  if (!isRecord(raw)) return null;

  const id = readText(raw.id);
  const codigo = readFirstText(raw, "claim", "codigo");
  if (!id && !codigo) return null;

  return {
    id: id || codigo,
    codigo,
    nome: readFirstText(raw, "label", "nome"),
    modulo: readFirstText(raw, "modulo"),
    descricao: readFirstText(raw, "description", "descricao"),
  };
}

export async function listPermissions({
  pesquisa,
  page,
  pageSize,
  sort,
  signal,
}: ListPermissionsParams = {}): Promise<PermissionsPage> {
  const term = pesquisa?.trim();

  const params: Record<string, string | number> = {};
  if (term) params.pesquisa = term;
  Object.assign(params, toPaginatedQuery({ page, pageSize, sort }));

  const config: HttpRequestConfig = { signal };
  if (Object.keys(params).length > 0) config.params = params;

  const response = await getHttpClient().get<unknown>(PERMISSION_PATHS.LIST, config);

  // A ordem é a do serviço (`sort_by`), que ordena o conjunto inteiro antes de paginar.
  const permissions = extractItems(response.data, COLLECTION_KEYS)
    .map(toPermission)
    .filter((permission): permission is Permission => permission !== null);

  return {
    permissions,
    total: extractTotal(response.data) ?? permissions.length,
    arvore: extractTree(response.data),
  };
}

/** Conjunto completo do resultado da consulta em vigor, página a página, na ordem que o serviço devolve. */
export async function listAllPermissions(
  params: Omit<ListPermissionsParams, "page" | "pageSize"> = {},
): Promise<Permission[]> {
  return collectAllPages((page, pageSize) =>
    listPermissions({ ...params, page, pageSize }).then(({ permissions: items, total }) => ({
      items,
      total,
    })),
  );
}

/**
 * Exportar Visualização (HU 1442): o servidor gera o arquivo com a busca e a ordenação em vigor, sem paginação, e
 * registra a exportação na auditoria. O nome vem do `Content-Disposition`.
 */
export async function exportPermissions({
  pesquisa,
  sort = DEFAULT_PERMISSION_SORT,
  filtros,
  colunas,
  format,
  signal,
}: ExportPermissionsParams): Promise<ExportedFile> {
  const term = pesquisa?.trim();
  const params: Record<string, string | string[]> = {};
  if (term) params.pesquisa = term;
  Object.assign(params, toPaginatedQuery({ sort }));
  params.formato = format;
  appendExportColumns(params, colunas, PERMISSIONS_EXPORT_COLUMN_KEYS, "Permissões");
  appendReportQuery(params, filtros);

  const response = await getHttpClient().get<ArrayBuffer>(PERMISSION_PATHS.EXPORT, {
    signal,
    params,
    responseType: "arraybuffer",
  });

  return {
    content: new Uint8Array(response.data),
    fileName: parseContentDispositionFileName(response.headers?.["content-disposition"]),
    mimeType: response.headers?.["content-type"] ?? null,
  };
}

/**
 * Atualiza rótulo e descrição (`{ label, description }`); a claim não é aceita pela API.
 * A descrição vai sempre: vazia, o core limpa o campo.
 *
 * Retorna a permissão devolvida pela API ou `null` quando a resposta não traz
 * corpo utilizável (ex.: 204), cabendo ao chamador reaproveitar o que enviou.
 */
export async function updatePermission({
  id,
  nome,
  descricao,
  signal,
}: UpdatePermissionParams): Promise<Permission | null> {
  const body = { label: nome.trim(), description: descricao.trim() };

  const response = await getHttpClient().put<unknown>(buildPermissionPath(id), body, { signal });

  return toPermission(extractResource(response.data));
}
